/**
 * Free Gift with Purchase
 * Adds a gift when trigger products are in the cart and a required discount code is active.
 * Gift quantity matches trigger line item quantities (e.g. qty 3 add → up to 3 gifts).
 */

(function() {
  'use strict';

  const settings = window.theme?.settings || {};
  const enableFreeGift = settings.enableFreeGift || false;
  const giftProductHandle = settings.freeGiftProduct || null;
  const requiredDiscountCode = (settings.freeGiftDiscountCode || '').trim().toLowerCase();
  const maxGiftQuantity = settings.freeGiftMaxQuantity || 3;

  const triggerProductHandles = (() => {
    let handles = [];

    if (Array.isArray(settings.freeGiftTriggerProducts)) {
      handles = settings.freeGiftTriggerProducts.filter(Boolean);
    } else if (settings.freeGiftTriggerProduct) {
      handles = [settings.freeGiftTriggerProduct];
    }

    return handles.filter((handle) => handle !== giftProductHandle);
  })();

  if (!enableFreeGift || triggerProductHandles.length === 0 || !giftProductHandle || !requiredDiscountCode) {
    return;
  }

  const productIdCache = {};
  const variantIdCache = {};
  let isSyncingGift = false;
  let isRefreshingCart = false;
  let lastCheckTime = 0;
  let scheduledCheckTimer = null;
  const CHECK_THROTTLE = 500;

  function normalizeCode(code) {
    return (code || '').trim().toLowerCase();
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function isDiscountCodeActive(cart) {
    const cookieCode = normalizeCode(getCookie('discount_code'));
    if (cookieCode === requiredDiscountCode) {
      return true;
    }

    if (Array.isArray(cart?.discount_codes)) {
      return cart.discount_codes.some((entry) => {
        return normalizeCode(entry.code) === requiredDiscountCode && entry.applicable !== false;
      });
    }

    if (Array.isArray(cart?.cart_level_discount_applications)) {
      return cart.cart_level_discount_applications.some((discount) => {
        return normalizeCode(discount.title) === requiredDiscountCode;
      });
    }

    return false;
  }

  function setDiscountMessage(message, isError = false) {
    document.querySelectorAll('[data-free-gift-discount-message]').forEach((element) => {
      element.textContent = message;
      element.classList.toggle('hidden', !message);
      element.classList.toggle('cart__free-gift-discount__message--error', isError);
    });
  }

  async function getCartJSON() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await response.json();
    } catch (error) {
      console.error('Free Gift: Error fetching cart:', error);
      return null;
    }
  }

  async function getProductIdFromHandle(productHandle) {
    if (productIdCache[productHandle]) {
      return productIdCache[productHandle];
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      const product = await response.json();
      if (product.id) {
        productIdCache[productHandle] = product.id;
        return product.id;
      }
    } catch (error) {
      console.error('Free Gift: Error fetching product:', error);
    }

    return null;
  }

  async function getTriggerProductIds() {
    const ids = await Promise.all(triggerProductHandles.map(getProductIdFromHandle));
    return new Set(ids.filter(Boolean).map(String));
  }

  async function getGiftProductId() {
    return getProductIdFromHandle(giftProductHandle);
  }

  async function getTriggerProductQuantity(cart) {
    if (!cart?.items) {
      return 0;
    }

    const triggerProductIds = await getTriggerProductIds();
    const giftProductId = String((await getGiftProductId()) || '');

    if (triggerProductIds.size === 0) {
      return 0;
    }

    return cart.items.reduce((total, item) => {
      const productId = String(item.product_id);

      if (productId === giftProductId || !triggerProductIds.has(productId)) {
        return total;
      }

      return total + (item.quantity || 0);
    }, 0);
  }

  function getGiftLineItems(cart, giftProductId) {
    if (!cart?.items || !giftProductId) {
      return [];
    }

    return cart.items.filter((item) => String(item.product_id) === String(giftProductId));
  }

  async function getGiftProductQuantity(cart) {
    const giftProductId = await getGiftProductId();
    return getGiftLineItems(cart, giftProductId).reduce((total, item) => total + (item.quantity || 0), 0);
  }

  async function getProductVariantId(productHandle) {
    if (variantIdCache[productHandle]) {
      return variantIdCache[productHandle];
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      const product = await response.json();
      if (product.variants?.length > 0) {
        const availableVariant = product.variants.find((variant) => variant.available) || product.variants[0];
        variantIdCache[productHandle] = availableVariant.id;
        return availableVariant.id;
      }
    } catch (error) {
      console.error('Free Gift: Error fetching product variant:', error);
    }

    return null;
  }

  async function updateCartItems(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    const result = await response.json();
    if (result.status) {
      throw new Error(result.description || result.message || 'Failed to update cart');
    }

    return result;
  }

  function refreshCart() {
    if (isRefreshingCart) {
      return true;
    }

    isRefreshingCart = true;

    if (window.cart && typeof window.cart.getCart === 'function') {
      try {
        window.cart.getCart();
        setTimeout(() => {
          isRefreshingCart = false;
        }, 1000);
        return true;
      } catch (error) {
        console.error('Free Gift: Error calling getCart:', error);
        isRefreshingCart = false;
      }
    }

    return false;
  }

  async function addGiftToCart(quantity = 1) {
    if (quantity <= 0) {
      return null;
    }

    const giftVariantId = await getProductVariantId(giftProductHandle);
    if (!giftVariantId) {
      console.error('Free Gift: Could not find variant for gift product');
      return null;
    }

    const formData = new FormData();
    formData.append('id', giftVariantId);
    formData.append('quantity', quantity);

    const response = await fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    });

    const result = await response.json();
    if (result.status) {
      throw new Error(result.description || result.message || 'Failed to add gift');
    }

    return result;
  }

  function needsGiftSync(cart, targetQuantity, giftProductId) {
    const giftLineItems = getGiftLineItems(cart, giftProductId);
    const giftQuantity = giftLineItems.reduce((total, item) => total + (item.quantity || 0), 0);

    return targetQuantity !== giftQuantity || giftLineItems.length > 1;
  }

  async function syncGiftQuantity(cart, targetQuantity) {
    const giftProductId = await getGiftProductId();
    const giftLineItems = getGiftLineItems(cart, giftProductId);
    const currentQuantity = giftLineItems.reduce((total, item) => total + (item.quantity || 0), 0);

    if (targetQuantity === currentQuantity && giftLineItems.length === 1) {
      return;
    }

    if (giftLineItems.length > 0) {
      const updates = {};
      giftLineItems.forEach((item) => {
        updates[item.key] = 0;
      });
      await updateCartItems(updates);
    }

    if (targetQuantity > 0) {
      await addGiftToCart(targetQuantity);
    }
  }

  async function applyDiscountCode(code) {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) {
      return false;
    }

    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });

      const discountActive = normalizeCode(getCookie('discount_code')) === requiredDiscountCode;
      if (discountActive) {
        setDiscountMessage('Discount applied. Your free gift will be added shortly.');
        scheduleCheck();
        refreshCart();
        return true;
      }

      setDiscountMessage('That discount code could not be applied.', true);
      return false;
    } catch (error) {
      console.error('Free Gift: Error applying discount code:', error);
      setDiscountMessage('That discount code could not be applied.', true);
      return false;
    }
  }

  async function checkAndSyncGift() {
    if (isSyncingGift) {
      return;
    }

    const now = Date.now();
    if (now - lastCheckTime < CHECK_THROTTLE) {
      return;
    }
    lastCheckTime = now;

    const cart = await getCartJSON();
    if (!cart) {
      return;
    }

    const giftProductId = await getGiftProductId();
    const discountActive = isDiscountCodeActive(cart);
    const triggerQuantity = await getTriggerProductQuantity(cart);
    const targetQuantity = discountActive ? Math.min(triggerQuantity, maxGiftQuantity) : 0;

    if (!needsGiftSync(cart, targetQuantity, giftProductId)) {
      return;
    }

    isSyncingGift = true;

    try {
      await syncGiftQuantity(cart, targetQuantity);

      const updatedCart = await getCartJSON();
      if (updatedCart && needsGiftSync(updatedCart, targetQuantity, giftProductId)) {
        await syncGiftQuantity(updatedCart, targetQuantity);
      }

      setTimeout(() => {
        if (!refreshCart()) {
          isRefreshingCart = false;
        }
      }, 300);
    } catch (error) {
      console.error('Free Gift: Error syncing gift:', error);
    } finally {
      isSyncingGift = false;
    }
  }

  function scheduleCheck(delay = 500) {
    if (scheduledCheckTimer) {
      clearTimeout(scheduledCheckTimer);
    }

    scheduledCheckTimer = setTimeout(() => {
      scheduledCheckTimer = null;
      checkAndSyncGift();
    }, delay);
  }

  function bindDiscountInputs() {
    document.querySelectorAll('[data-free-gift-discount-input]').forEach((input) => {
      const cookieCode = getCookie('discount_code');
      if (cookieCode && !input.value) {
        input.value = cookieCode;
      }
    });
  }

  function init() {
    bindDiscountInputs();

    const urlDiscount = new URLSearchParams(window.location.search).get('discount');
    if (urlDiscount) {
      scheduleCheck();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        bindDiscountInputs();
        scheduleCheck();
      });
    } else {
      scheduleCheck();
    }

    document.addEventListener('click', (event) => {
      const applyButton = event.target.closest('[data-free-gift-discount-apply]');
      if (applyButton) {
        event.preventDefault();
        const wrapper = applyButton.closest('[data-free-gift-discount]');
        const input = wrapper?.querySelector('[data-free-gift-discount-input]');
        if (input?.value) {
          applyDiscountCode(input.value.trim());
        }
        return;
      }

      const button = event.target.closest('[data-add-to-cart], button[type="submit"][name="add"]');
      if (button && !button.disabled) {
        scheduleCheck(800);
      }
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;

      if (form?.matches?.('[data-free-gift-discount-form]')) {
        event.preventDefault();
        const input = form.querySelector('[data-free-gift-discount-input]');
        if (input?.value) {
          applyDiscountCode(input.value.trim());
        }
        return;
      }

      if (form?.matches?.('form[action*="cart/add"], form[data-type="add-to-cart-form"]')) {
        scheduleCheck(800);
      }
    }, true);

    ['theme:cart:added', 'theme:cart:add', 'theme:cart:loaded', 'cart:item-added'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        bindDiscountInputs();
        scheduleCheck(800);
      });
    });

    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];

      if (typeof url === 'string' && url.includes('/discount/')) {
        const fetchPromise = originalFetch.apply(this, args);
        fetchPromise.then((response) => {
          if (response.ok || response.status === 302) {
            scheduleCheck();
          }
        }).catch(() => scheduleCheck());
        return fetchPromise;
      }

      if (typeof url === 'string' && url.includes('/cart/add')) {
        const fetchPromise = originalFetch.apply(this, args);
        fetchPromise
          .then((response) => {
            if (response.ok) {
              response.clone().json().then((data) => {
                if (data && !data.status) {
                  scheduleCheck(800);
                }
              }).catch(() => scheduleCheck(800));
            }
          })
          .catch(() => scheduleCheck(800));
        return fetchPromise;
      }

      if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update'))) {
        const fetchPromise = originalFetch.apply(this, args);
        fetchPromise.then((response) => {
          if (response.ok && !isSyncingGift) {
            scheduleCheck();
          }
        }).catch(() => {
          if (!isSyncingGift) {
            scheduleCheck();
          }
        });
        return fetchPromise;
      }

      return originalFetch.apply(this, args);
    };

    const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('[data-cart-drawer]');
    if (cartDrawer) {
      const cartObserver = new MutationObserver((mutations) => {
        const hasRelevantChanges = mutations.some((mutation) => mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0);
        if (hasRelevantChanges && !isSyncingGift) {
          bindDiscountInputs();
          scheduleCheck(1000);
        }
      });

      cartObserver.observe(cartDrawer, {
        childList: true,
        subtree: true,
      });
    }
  }

  init();
})();
